import { useEffect, useMemo, useState } from 'react';
import { Loader2, History } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Card } from '../../ui/card';

import type { ShortcutRunRecord } from './types';
import { getShortcutStatusTone } from './types';

type ShortcutRunStatusProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  run: ShortcutRunRecord | null;
  onOpenHistory: () => void;
};

const toneClasses = {
  amber: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  blue: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  green: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  red: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  slate: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
} as const;

function formatElapsed(createdAt: string) {
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(createdAt).getTime()) / 1000));
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}:${String(remainder).padStart(2, '0')}`;
}

export default function ShortcutRunStatus({ open, onOpenChange, run, onOpenHistory }: ShortcutRunStatusProps) {
  const [elapsed, setElapsed] = useState(run ? formatElapsed(run.created_at) : '0:00');

  useEffect(() => {
    if (!run || !open) {
      return;
    }

    setElapsed(formatElapsed(run.created_at));
    const timer = window.setInterval(() => setElapsed(formatElapsed(run.created_at)), 1000);
    return () => window.clearInterval(timer);
  }, [open, run]);

  const tone = useMemo(() => (run ? getShortcutStatusTone(run.status) : 'slate'), [run]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl border-slate-700 bg-slate-950 text-slate-100">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Loader2 className={`h-5 w-5 ${run?.status === 'running' ? 'animate-spin text-sky-400' : 'text-emerald-400'}`} />
            Shortcut job status
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            MapleSheet keeps polling this run in the background even if you close this panel.
          </DialogDescription>
        </DialogHeader>

        {run ? (
          <div className="space-y-4">
            <Card className="space-y-3 border-slate-800 bg-slate-900/70 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500">Run ID</div>
                  <div className="font-mono text-sm text-slate-100">{run.shortcut_run_id}</div>
                </div>
                <Badge className={toneClasses[tone]}>{run.status}</Badge>
              </div>

              <div className="grid gap-3 text-sm text-slate-300 sm:grid-cols-2">
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500">Elapsed</div>
                  <div>{elapsed}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500">Selection</div>
                  <div>{run.selected_range || 'Used sheet'}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500">Source</div>
                  <div>{run.source_file_name || 'Current MapleSheet'}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500">Prompt</div>
                  <div className="line-clamp-2">{run.prompt}</div>
                </div>
              </div>
            </Card>

            {run.error_message ? (
              <Card className="border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-200">
                {run.error_message}
              </Card>
            ) : null}
          </div>
        ) : null}

        <DialogFooter className="flex-col gap-3 sm:flex-row sm:justify-between">
          <Button type="button" variant="outline" className="border-slate-700 bg-slate-900 text-slate-100 hover:bg-slate-800" onClick={onOpenHistory}>
            <History className="mr-2 h-4 w-4" />
            Open history
          </Button>
          <Button type="button" className="bg-slate-100 text-slate-950 hover:bg-white" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
